import React, { createContext, useContext, useState, useEffect } from 'react';
import { getDomainConfigByHostname, DOMAIN_CONFIGS } from '../config';
import { faviconManager } from '../utils/faviconManager';
import { logoManager } from '../utils/logoManager';
import { iconManager } from '../utils/iconManager';
import { setCurrentDomain } from '../utils/imageManager';

const ThemeContext = createContext();

// Helper to get initial config (works during pre-rendering and runtime)
const getInitialConfig = () => {
  // Check for environment variable first (set during build)
  const envDomainId = process.env.REACT_APP_DOMAIN_ID;
  if (envDomainId) {
    const config = Object.values(DOMAIN_CONFIGS).find(c => c.id === envDomainId);
    if (config) return config;
  }
  
  // Check for window (runtime in browser)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    return getDomainConfigByHostname(hostname);
  }
  
  // Fallback to fleet-dashcam config for pre-rendering
  return DOMAIN_CONFIGS['fleet-dashcam'] || Object.values(DOMAIN_CONFIGS)[0] || null;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize with config immediately (supports pre-rendering)
  const [domainConfig, setDomainConfig] = useState(() => getInitialConfig());
  const [isLoading, setIsLoading] = useState(false);
  const [isDevelopment, setIsDevelopment] = useState(false);

  useEffect(() => {
    // Only run in browser
    if (typeof window === 'undefined') return;
    
    // Detect if we're in development or production
    const hostname = window.location.hostname;
    const isDev = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost');
    setIsDevelopment(isDev);

    // If we already have a config from REACT_APP_DOMAIN_ID (pre-rendering/build-time),
    // don't override it with hostname detection (which would be localhost during pre-rendering)
    const envDomainId = process.env.REACT_APP_DOMAIN_ID;
    if (envDomainId && domainConfig?.id === envDomainId) {
      // Already have the correct config from build-time, just apply it
      setIsLoading(false);
      setCurrentDomain(domainConfig.id);
      applyDomainConfig(domainConfig).catch(error => {
        // Failed to apply domain config
      });
      return;
    }

    // Only update config if we don't have a build-time domain ID
    // (runtime hostname-based detection for dev/production without pre-rendering)
    const config = getDomainConfigByHostname(hostname);
    if (config && config.id !== domainConfig?.id) {
      setDomainConfig(config);
    }
    setIsLoading(false);
    
    // Set current domain for image tracking
    if (config?.id) {
      setCurrentDomain(config.id);
    }

    // Apply domain configuration
    applyDomainConfig(config).catch(error => {
      // Failed to apply domain config
    });

  }, []);

  const applyDomainConfig = async (config) => {
    if (!config) return;

    // Update favicon and meta tags using FaviconManager
    await faviconManager.updateFavicon(config);
    
    // Preload logos for better performance
    logoManager.preloadAllLogos(config).catch(error => {
      // Failed to preload logos
    });
  };

  const switchDomain = (domainId) => {
    // Only allow domain switching in development mode
    if (!isDevelopment) {
      console.warn('Domain switching is only available in development mode');
      return;
    }
    
    const config = Object.values(DOMAIN_CONFIGS).find(c => c.id === domainId);
    if (config) {
      
      // Set current domain for image tracking (this will clear previous tracking data)
      setCurrentDomain(domainId);
      
      setDomainConfig(config);
      applyDomainConfig(config).catch(error => {
        // Failed to apply domain config
      });
    }
  };

  const brandId = domainConfig?.id || 'fleet-dashcam';
  

  const value = {
    domainConfig,
    isLoading,
    isDevelopment,
    switchDomain,
    brand: {
      id: brandId,
      name: domainConfig?.name || 'Fleet Dash Cam',
      tagline: domainConfig?.tagline || 'Professional Fleet Dash Cameras',
      description: domainConfig?.description || 'Advanced dash cameras for fleet safety and security.',
      logo: domainConfig?.logo || '/assets/logos/fleet-dashcam-logo',
      whiteLogo: domainConfig?.whiteLogo || '/assets/logos/fleet-dashcam-white-logo',
      favicon: domainConfig?.favicon || '/assets/favicons/fleet-dashcam-favicon.png',
      icon: domainConfig?.icon || '/assets/icons/fleet-dashcam',
      getLogo: (context) => logoManager.getLogo(domainConfig, context),
      getLogoWithFallback: (context, onError) => logoManager.getLogoWithFallback(domainConfig, context, onError),
      getIcon: () => iconManager.getDomainIcon(domainConfig?.id),
      getIconWithFallback: (fallbackIcon) => iconManager.getIconWithFallback(domainConfig?.id, fallbackIcon)
    },
    navigation: domainConfig?.navigation || [],
    pages: domainConfig?.pages || [],
    cta: domainConfig?.cta || { primary: 'Request Demo', secondary: 'Learn More' }
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
